import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type SiteSummary = {
  id: string;
  name: string;
  has_active_menu: boolean;
  /** The Occupancy page's capacity line and its over-capacity reddening (POS2-1259 decision 14).
   *  Null means unset, which draws no line — carried here rather than fetched separately so one
   *  number does not cost a second sites query. */
  kitchen_capacity: number | null;
};

type Kitchen = {
  id: string;
  site_id: string;
  name: string;
};

export function useSitesWithActiveMenus() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const orgId = useAdminOrgStore((s) => s.orgId);
  return useQuery<SiteSummary[]>({
    queryKey: ["sites-active-menu", orgId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: SiteSummary[] };
      return body.data.map((s) => ({
        id: s.id,
        name: s.name,
        has_active_menu: s.has_active_menu ?? false,
        kitchen_capacity: s.kitchen_capacity ?? null,
      }));
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    staleTime: 60_000,
  });
}

export function useKitchens(siteId: string | null) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const orgId = useAdminOrgStore((s) => s.orgId);
  return useQuery<Kitchen[]>({
    queryKey: ["kitchens-by-site", orgId, siteId ?? "all"],
    queryFn: async () => {
      const url = siteId
        ? `${API_URL}/api/v1/kitchens?site_id=${siteId}`
        : `${API_URL}/api/v1/kitchens`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: Kitchen[] };
      return body.data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    staleTime: 60_000,
  });
}
